import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AttachmentPanel from "./AttachmentPanel";
import MentionTextarea, { renderWithMentions } from "./MentionTextarea";
import IssueLinks from "./IssueLinks";
import TimeTracking from "./TimeTracking";
import RichTextEditor from "./RichTextEditor";
import MarkdownRenderer from "./MarkdownRenderer";
import { useRealtime } from "@/lib/realtime";
import { useCan } from "@/lib/perm";
import { useSettings, findType, findPriority, findStatus } from "@/lib/settings";
import { ISSUE_TYPES, PRIORITIES, STATUSES, typeMeta, prioMeta, initials } from "@/lib/meta";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trash2, Send, X, ExternalLink, Eye, EyeOff, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function IssueDetailDialog({ issueId, setIssueId, users, projects }) {
  const { user } = useAuth();
  const canDelete = useCan("issue.delete");
  const s = useSettings() || {};
  const TYPES = (s.issueTypes && s.issueTypes.length) ? s.issueTypes : ISSUE_TYPES;
  const PRIOS = (s.priorities && s.priorities.length) ? s.priorities : PRIORITIES;
  const STATS = (s.statuses && s.statuses.length) ? s.statuses : STATUSES;
  const [issue, setIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [watchersData, setWatchersData] = useState({ watchers: [], is_watching: false, count: 0 });
  const [comment, setComment] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const open = !!issueId;

  const load = async () => {
    if (!issueId) return;
    const [i, c, a, w] = await Promise.all([
      api.get(`/issues/${issueId}`),
      api.get(`/issues/${issueId}/comments`),
      api.get(`/issues/${issueId}/activity`),
      api.get(`/issues/${issueId}/watchers`).catch(() => ({ data: { watchers: [], is_watching: false, count: 0 } })),
    ]);
    setIssue(i.data); setComments(c.data); setActivity(a.data);
    setWatchersData(w.data || { watchers: [], is_watching: false, count: 0 });
    setDescDraft(i.data.description || "");
    setSummaryDraft(i.data.summary || "");
  };
  useEffect(() => { if (open) load(); else { setIssue(null); setComments([]); setActivity([]); setWatchersData({ watchers: [], is_watching: false, count: 0 }); } }, [issueId]);

  useRealtime((ev) => {
    if (!open || !issue) return;
    if ((ev.type === "comment.new" || ev.type === "comment.deleted") && ev.issue_id === issue.id) { load(); }
    else if ((ev.type === "issue.updated" || ev.type === "issue.deleted") && ev.issue_id === issue.id) {
      if (ev.type === "issue.deleted") setIssueId(null); else load();
    }
  }, [issue?.id, open]);

  const toggleWatch = async () => {
    if (!issue || !user) return;
    try {
      if (watchersData.is_watching) {
        const res = await api.delete(`/issues/${issue.id}/watchers/${user.id}`);
        setWatchersData(prev => ({
          ...prev,
          is_watching: false,
          watchers: res.data.watchers || prev.watchers.filter(w => w.id !== user.id),
          count: res.data.count ?? Math.max(0, prev.count - 1),
        }));
        toast.success("Stopped watching issue");
      } else {
        const res = await api.post(`/issues/${issue.id}/watchers`, { user_id: user.id });
        setWatchersData(prev => ({
          ...prev,
          is_watching: true,
          watchers: res.data.watchers || [...prev.watchers, user],
          count: res.data.count ?? (prev.count + 1),
        }));
        toast.success("You are now watching this issue");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update watchers");
    }
  };

  const addWatcher = async (targetUserId) => {
    if (!issue || !targetUserId) return;
    try {
      const res = await api.post(`/issues/${issue.id}/watchers`, { user_id: targetUserId });
      setWatchersData(prev => ({
        ...prev,
        watchers: res.data.watchers,
        is_watching: targetUserId === user?.id ? true : prev.is_watching,
        count: res.data.count,
      }));
      toast.success("Watcher added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add watcher");
    }
  };

  const removeWatcher = async (targetUserId) => {
    if (!issue || !targetUserId) return;
    try {
      const res = await api.delete(`/issues/${issue.id}/watchers/${targetUserId}`);
      setWatchersData(prev => ({
        ...prev,
        watchers: res.data.watchers,
        is_watching: targetUserId === user?.id ? false : prev.is_watching,
        count: res.data.count,
      }));
      toast.success("Watcher removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove watcher");
    }
  };

  const patch = async (upd) => {
    const r = await api.patch(`/issues/${issue.id}`, upd);
    setIssue(r.data);
    const a = await api.get(`/issues/${issue.id}/activity`); setActivity(a.data);
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/issues/${issue.id}/comments`, { body: comment });
    setComment("");
    load();
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      toast.success("Comment deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete comment");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${issue.key}?`)) return;
    await api.delete(`/issues/${issue.id}`);
    toast.success("Issue deleted");
    setIssueId(null);
    window.dispatchEvent(new Event("ft:issues-changed"));
  };

  const proj = projects.find(p => p.id === issue?.project_id);
  const assignee = users.find(u => u.id === issue?.assignee_id);
  const reporter = users.find(u => u.id === issue?.reporter_id);
  const T = issue ? findType(TYPES, issue.issue_type) : null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && setIssueId(null)}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-y-auto" data-testid="issue-detail">
        {issue && T && (
          <div className="flex flex-col h-full">
            <div className="border-b border-border p-4 flex items-center gap-3">
              <T.icon className={cn("w-5 h-5", T.cls)} />
              <span className="font-mono text-xs text-muted-foreground">{issue.key}</span>
              <div className="flex-1" />
              <Button
                variant={watchersData.is_watching ? "secondary" : "ghost"}
                size="sm"
                onClick={toggleWatch}
                className={cn("gap-1.5 text-xs h-8 px-2.5", watchersData.is_watching && "bg-primary/10 text-primary hover:bg-primary/20")}
                title={watchersData.is_watching ? "Watching issue (receiving notifications)" : "Watch this issue to receive email notifications"}
                data-testid="dialog-toggle-watch-btn"
              >
                {watchersData.is_watching ? <Eye className="w-3.5 h-3.5 text-primary" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                <span>{watchersData.count || 0}</span>
              </Button>
              <a
                href={`/issues/${issue.key || issue.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open full page in new tab"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground h-8 w-8 text-muted-foreground hover:text-foreground"
                data-testid="issue-open-new-tab"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
              {canDelete ? <Button variant="ghost" size="icon" onClick={del} className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid="issue-delete"><Trash2 className="w-4 h-4" /></Button> : null}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={()=>setIssueId(null)}><X className="w-4 h-4" /></Button>
            </div>
            <SheetHeader className="px-6 pt-5 pb-2">
              {editingSummary ? (
                <Input value={summaryDraft} autoFocus onChange={(e)=>setSummaryDraft(e.target.value)}
                  onBlur={async ()=>{ if (summaryDraft!==issue.summary) await patch({summary:summaryDraft}); setEditingSummary(false); }}
                  onKeyDown={(e)=>{ if(e.key==="Enter") e.currentTarget.blur(); if(e.key==="Escape"){setSummaryDraft(issue.summary); setEditingSummary(false);} }}
                  className="text-lg font-display font-semibold h-10" data-testid="issue-summary-input" />
              ) : (
                <SheetTitle className="text-lg font-display font-semibold cursor-pointer hover:bg-accent -mx-2 px-2 rounded" onClick={()=>setEditingSummary(true)} data-testid="issue-summary">
                  {issue.summary}
                </SheetTitle>
              )}
            </SheetHeader>
            <div className="grid grid-cols-3 gap-6 px-6 pb-6 flex-1">
              <div className="col-span-2 space-y-6">
                <div>
                  <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-2">Description</div>
                  {editingDesc ? (
                    <div className="space-y-2">
                      <RichTextEditor
                        value={descDraft}
                        onChange={setDescDraft}
                        issueId={issue.id}
                        users={users}
                        minRows={6}
                        testid="issue-desc-textarea"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={async()=>{ await patch({description:descDraft}); setEditingDesc(false); }}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={()=>{setDescDraft(issue.description||""); setEditingDesc(false);}}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div onClick={()=>setEditingDesc(true)} className="min-h-[80px] p-3 rounded-md border border-transparent hover:border-border hover:bg-accent/40 cursor-text text-sm" data-testid="issue-desc">
                      {issue.description ? (
                        <MarkdownRenderer content={issue.description} users={users} />
                      ) : (
                        <span className="text-muted-foreground">Add a description… (supports rich text, images, tables, code)</span>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-2">Attachments</div>
                  <AttachmentPanel issueId={issue.id} />
                </div>
                <IssueLinks issueId={issue.id} />
                <TimeTracking issue={issue} onIssuePatched={load} />
                <Tabs defaultValue="comments">
                  <TabsList>
                    <TabsTrigger value="comments">Comments ({comments.length})</TabsTrigger>
                    <TabsTrigger value="activity">Activity ({activity.length})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="comments" className="space-y-3 pt-3">
                    {comments.map(c => {
                      const author = users.find(u => u.id === c.author_id);
                      const canDeleteComment = user && (user.id === c.author_id || user.role === "admin" || user.role === "owner");
                      return (
                        <div key={c.id} className="group relative flex gap-3 p-3 rounded-lg border bg-muted/20 hover:bg-muted/30 transition-colors">
                          <Avatar className="w-7 h-7"><AvatarImage src={author?.avatar_url} /><AvatarFallback className="text-[10px]">{initials(author?.name)}</AvatarFallback></Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="text-xs"><span className="font-semibold text-foreground">{author?.name || "Unknown"}</span> <span className="text-muted-foreground">· {new Date(c.created_at).toLocaleString()}</span></div>
                              {canDeleteComment && (
                                <button
                                  type="button"
                                  onClick={() => deleteComment(c.id)}
                                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all p-1 rounded hover:bg-destructive/10 -mt-1 -mr-1"
                                  title="Delete comment"
                                  data-testid={`delete-comment-${c.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <div className="text-sm mt-1 leading-relaxed">
                              <MarkdownRenderer content={c.body} users={users} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="space-y-2 pt-2">
                      <RichTextEditor
                        value={comment}
                        onChange={setComment}
                        issueId={issue.id}
                        users={users}
                        placeholder="Add a comment… (paste or drag & drop images, format text, / commands, @ mentions)"
                        minRows={3}
                        testid="comment-input"
                      />
                      <div className="flex justify-end">
                        <Button size="sm" onClick={addComment} data-testid="comment-submit" className="gap-1.5">
                          <Send className="w-3.5 h-3.5" /> Save Comment
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="activity" className="pt-3 space-y-2">
                    {activity.map(a => {
                      const u = users.find(x => x.id === a.user_id);
                      return (
                        <div key={a.id} className="text-xs text-muted-foreground flex gap-2">
                          <span className="text-foreground font-medium">{u?.name || "Someone"}</span>
                          <span>{a.action}{a.field ? ` ${a.field}` : ""}{a.from_val && a.to_val ? `: ${a.from_val} → ${a.to_val}` : ""}</span>
                          <span className="ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </TabsContent>
                </Tabs>
              </div>
              <div className="space-y-4 text-sm">
                <Field label="Status">
                  <Select value={issue.status} onValueChange={(v)=>patch({status:v})}>
                    <SelectTrigger className="h-8" data-testid="issue-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATS.map(s => <SelectItem key={s.id} value={s.id}>{s.label || s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Assignee">
                  <Select value={issue.assignee_id || "unassigned"} onValueChange={(v)=>patch({assignee_id: v==="unassigned" ? null : v})}>
                    <SelectTrigger className="h-8" data-testid="issue-assignee"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={issue.priority} onValueChange={(v)=>patch({priority:v})}>
                    <SelectTrigger className="h-8" data-testid="issue-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIOS.map(p => <SelectItem key={p.id} value={p.id}><div className="flex items-center gap-2"><p.icon className={`w-4 h-4 ${p.cls}`} />{p.label || p.name}</div></SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Type">
                  <Select value={issue.issue_type} onValueChange={(v)=>patch({issue_type:v})}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><t.icon className={`w-4 h-4 ${t.cls}`} />{t.label || t.name}</div></SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Story points">
                  <Input type="number" defaultValue={issue.story_points || ""} onBlur={(e)=>patch({story_points: e.target.value ? Number(e.target.value) : null})} className="h-8" />
                </Field>
                {issue.issue_type === "epic" && (
                  <>
                    <Field label="Start date">
                      <Input type="date" defaultValue={issue.start_date ? issue.start_date.slice(0,10) : ""} onBlur={(e)=>patch({start_date: e.target.value ? new Date(e.target.value).toISOString() : null})} className="h-8" data-testid="issue-start-date" />
                    </Field>
                    <Field label="Target date">
                      <Input type="date" defaultValue={issue.target_date ? issue.target_date.slice(0,10) : ""} onBlur={(e)=>patch({target_date: e.target.value ? new Date(e.target.value).toISOString() : null})} className="h-8" data-testid="issue-target-date" />
                    </Field>
                  </>
                )}
                <Field label="Due date">
                  <Input type="date" defaultValue={issue.due_date ? issue.due_date.slice(0,10) : ""} onBlur={(e)=>patch({due_date: e.target.value ? new Date(e.target.value).toISOString() : null})} className="h-8" />
                </Field>
                <Field label="Reporter"><div className="text-sm">{reporter?.name || "—"}</div></Field>
                <Field label={`Watchers (${watchersData.count || 0})`}>
                  <div className="space-y-1.5 mt-1">
                    <div className="flex flex-wrap gap-1 items-center">
                      {(watchersData.watchers || []).map((w) => (
                        <div key={w.id} className="inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-muted/60 border text-[11px]" title={w.email}>
                          <Avatar className="w-3.5 h-3.5">
                            <AvatarImage src={w.avatar_url} />
                            <AvatarFallback className="text-[7px]">{initials(w.name)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-foreground truncate max-w-[80px]">{w.name}</span>
                          {(user?.id === w.id || user?.role === "admin" || user?.role === "owner") && (
                            <button
                              type="button"
                              onClick={() => removeWatcher(w.id)}
                              className="text-muted-foreground hover:text-destructive ml-0.5"
                              title="Remove watcher"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      ))}
                      {(!watchersData.watchers || watchersData.watchers.length === 0) && (
                        <span className="text-[11px] text-muted-foreground italic">No watchers</span>
                      )}
                    </div>
                    <Select onValueChange={(val) => { if (val) addWatcher(val); }}>
                      <SelectTrigger className="h-6 text-[11px]">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <UserPlus className="w-2.5 h-2.5" />
                          <span>Add watcher…</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {users
                          .filter((u) => !(watchersData.watchers || []).some((w) => w.id === u.id))
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id} className="text-xs">
                              <div className="flex items-center gap-1.5">
                                <Avatar className="w-3.5 h-3.5">
                                  <AvatarImage src={u.avatar_url} />
                                  <AvatarFallback className="text-[7px]">{initials(u.name)}</AvatarFallback>
                                </Avatar>
                                <span>{u.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Field>
                <Field label="Created"><div className="text-xs text-muted-foreground">{new Date(issue.created_at).toLocaleString()}</div></Field>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
function Field({label, children}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-medium mb-1">{label}</div>
      {children}
    </div>
  );
}
